import React, { useEffect, useMemo, useRef, useState } from "react";
import "./mockup.css";
import PopupContent from "./PopupContent";

/** ========== NEAR MISS SPLIT: helper formatters ========== */
const fmtDdMmYyyyHhMm = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};
const monthNameFromInt = (m) => {
  const names = [null, "January","February","March","April","May","June","July","August","September","October","November","December"];
  // Spec says 2 = February, so 1 = January (standard)
  return names[m] || `Month ${m}`;
};

/** ========== NEAR MISS SPLIT: right panel component (on-demand fetch) ========== */
function NearMissMonthlyPanel({ pharmacyName }) {
  const [loading, setLoading] = useState(true);
  const [staffMap, setStaffMap] = useState({});
  const [reports, setReports] = useState([]);
  const [error, setError] = useState("");

  // Safely pick token once (no dependency on re-renders)
  const tokenRef = useRef(null);
  useEffect(() => {
    tokenRef.current = localStorage.getItem("authToken");
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setError("");
      try {
        const token = tokenRef.current;
        if (!token) throw new Error("Not authenticated. Please log in.");

        // --- Fetch monthly reports (JOIN to be done server-side in final route; for now we accept raw IDs) ---
         const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_monthly_report", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${tokenRef.current}`
    },
    body: JSON.stringify({ pharmacyName })   // <-- IMPORTANT
  });
        const text = await res.text();
        if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

        let json;
        try { json = JSON.parse(text); } catch (e) { throw new Error("Invalid JSON from monthly report endpoint"); }

        // Allow various shapes: {reports: [...] } | {data: [...]} | [...]
        const arr = Array.isArray(json) ? json
                  : Array.isArray(json.reports) ? json.reports
                  : Array.isArray(json.data) ? json.data
                  : [];

        // --- Optional: fetch minimal staff map if names not included in report payload ---
        const needsNames = arr.some(r => (!r.pharmacistName && r.pharmacistId) || (Array.isArray(r.otherStaff) && r.otherStaff.length && !r.otherStaffNames));
        let staffDict = {};
        if (needsNames) {
          try {
            const resStaff = await fetch("https://api.pharmacegroup.co.uk/staffs_min", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
              body: JSON.stringify({ pharmacyName })
            });
            if (resStaff.ok) {
              const txt = await resStaff.text();
              let js;
              try { js = JSON.parse(txt); } catch { js = {}; }
              const list = Array.isArray(js) ? js : Array.isArray(js.staffs) ? js.staffs : Array.isArray(js.data) ? js.data : [];
              staffDict = list.reduce((acc, s) => {
                const id = (s?._id?.$oid) || s?._id || s?.id;
                const name = s?.name || s?.fullName || s?.email || String(id || "");
                if (id) acc[id] = name;
                return acc;
              }, {});
            }
          } catch (_) {
            // non-fatal; we’ll fall back to IDs
          }
        }

        // Normalise & sort by createdAt DESC
        const norm = arr.map(r => {
          const created = r?.createdAt?.$date || r?.createdAt || r?.timestamp?.$date || r?.timestamp;
          const monthInt = r?.month;
          const year = r?.year;
          // Name resolution prefer server-provided fields if present
          let pharmacistName = r?.pharmacistName;
          if (!pharmacistName && r?.pharmacistId) {
            const pid = r.pharmacistId?.$oid || r.pharmacistId;
            pharmacistName = staffDict[pid] || pid || "";
          }
          // otherStaff could be array of ids or names
          let otherStaffNames = r?.otherStaffNames;
          if (!otherStaffNames && Array.isArray(r?.otherStaff)) {
            otherStaffNames = r.otherStaff.map(x => {
              const id = x?.$oid || x;
              return staffDict[id] || id;
            });
          }
          return {
            _raw: r,
            createdAt: created,
            createdAtMs: created ? new Date(created).getTime() : 0,
            monthName: monthNameFromInt(Number(monthInt)),
            year,
            prescriptionsDispensed: r?.prescriptionsDispensed ?? "-",
            nearMissPercentage: r?.nearMissPercentage ?? "-",
            description: r?.description ?? "-",
            cause: r?.cause ?? "-",
            actionPlan: r?.actionPlan ?? "-",
            actionPlanReview: r?.actionPlanReview ?? "-",
            pharmacistName: pharmacistName || "-",
            otherStaffNames: Array.isArray(otherStaffNames) ? otherStaffNames : []
          };
        }).sort((a, b) => b.createdAtMs - a.createdAtMs);

        if (!cancelled) {
          setStaffMap(staffDict);
          setReports(norm);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || String(err));
          setLoading(false);
        }
      }
    }

    fetchAll();
    return () => { cancelled = true; };
  }, [pharmacyName]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee", background: "#f9fafb", fontWeight: 600 }}>
        Monthly Near Miss Review
      </div>

      {loading ? (
        <div style={{ padding: 12, fontSize: 14 }}>Loading monthly reports…</div>
      ) : error ? (
        <div style={{ padding: 12, color: "#b91c1c" }}>Error: {error}</div>
      ) : reports.length === 0 ? (
        <div style={{ padding: 12, fontSize: 14 }}>No monthly reports found.</div>
      ) : (
        <div style={{ padding: 10, overflowY: "auto" }}>
          {reports.map((r, i) => (
            <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {fmtDdMmYyyyHhMm(r.createdAt)} • Month in review: {r.monthName} {r.year}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div><strong>Items scanned:</strong> {r.prescriptionsDispensed}</div>
                <div><strong>Near miss percentage:</strong> {typeof r.nearMissPercentage === "number" ? r.nearMissPercentage.toFixed(1) : r.nearMissPercentage}</div>
              </div>
              <div style={{ marginTop: 8 }}><strong>Description:</strong> {r.description}</div>
              <div style={{ marginTop: 4 }}><strong>Cause:</strong> {r.cause}</div>
              <div style={{ marginTop: 4 }}><strong>Action plan:</strong> {r.actionPlan}</div>
              <div style={{ marginTop: 4 }}><strong>Action plan review:</strong> {r.actionPlanReview}</div>
              <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div><strong>Pharmacist:</strong> {r.pharmacistName}</div>
                <div>
                  <strong>Staff attending:</strong>{" "}
                  {r.otherStaffNames && r.otherStaffNames.length
                    ? r.otherStaffNames.join(", ")
                    : "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** ========== NEAR MISS SPLIT: wrapper combining left (current) + right (monthly) ========== */
// NearMissSplitDialog
function NearMissSplitDialog({ popupData }) {
  const { pharmacyName, pharmacyId } = popupData;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, height: "70vh", minHeight: 420 }}>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee", background: "#f9fafb", fontWeight: 600 }}>
          Near Miss by Category
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 8 }}>
          {/* 👇 Force legacy column for PopupContent so charts render */}
          <PopupContent popupData={{ ...popupData, column: "Near Miss" }} />
        </div>
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        <NearMissMonthlyPanel pharmacyName={pharmacyName} pharmacyId={pharmacyId} />
      </div>
    </div>
  );
}

/** Self-contained PopupContent with column picker + flattening for dynamic datasets */
function PopupGrid({ popupData }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSize] = useState(15);
  const [pickerOpen, setPickerOpen] = useState(false);

  // --- helpers to flatten & normalise values so ALL fields display ---
  const normaliseVal = (v) => {
    if (v == null) return null;
    if (v instanceof Date) return v.toISOString();
    if (typeof v === "object") {
      if (v.$oid && typeof v.$oid === "string") return v.$oid;
      if (v.$date && typeof v.$date === "string") return v.$date;
    }
    return v;
  };
  const flattenRow = (row, prefix = "", out = {}, depth = 0) => {
    if (row == null) return out;
    if (depth > 3) { out[prefix.slice(0,-1)] = JSON.stringify(row); return out; }
    if (Array.isArray(row)) {
      out[prefix ? prefix.slice(0,-1) : "[]"] = row.map(x => typeof x === "object" ? JSON.stringify(x) : String(x)).join(" | ");
      row.slice(0,3).forEach((v, i) => {
        if (typeof v === "object") out[`${prefix}${i}`] = JSON.stringify(v);
        else out[`${prefix}${i}`] = normaliseVal(v);
      });
      return out;
    }
    if (typeof row !== "object") {
      out[prefix ? prefix.slice(0,-1) : "value"] = normaliseVal(row);
      return out;
    }
    for (const k of Object.keys(row)) {
      const v = row[k];
      if (v && typeof v === "object" && !Array.isArray(v)) {
        flattenRow(v, `${prefix}${k}.`, out, depth+1);
      } else {
        out[`${prefix}${k}`] = normaliseVal(v);
      }
    }
    return out;
  };

  // Normalise incoming shapes
  const rawRows = useMemo(() => {
    const p = popupData;
    if (!p) return [];
    if (Array.isArray(p)) return p;
    if (Array.isArray(p.answer)) return p.answer;
    if (Array.isArray(p.rows)) return p.rows;
    if (Array.isArray(p.data)) return p.data;
    if (Array.isArray(p.results)) return p.results;

    // Known API keys
    if (Array.isArray(p.fridgeLogs)) return p.fridgeLogs;
    if (Array.isArray(p.shelfcheckdetails)) return p.shelfcheckdetails;
    if (Array.isArray(p.shelfDetails)) return p.shelfDetails;
    if (Array.isArray(p.cleaningDetails)) return p.cleaningDetails;
    if (Array.isArray(p.handoverDetails)) return p.handoverDetails;
    if (Array.isArray(p.nearMissDetails)) return p.nearMissDetails;
    if (Array.isArray(p.stockDetails)) return p.stockDetails;

    // Special case: deliveryDetails.points is [lat,long] arrays
    if (p?.deliveryDetails?.points && Array.isArray(p.deliveryDetails.points)) {
      return p.deliveryDetails.points.map(([lat, long]) => ({ lat, long }));
    }

    return [];
  }, [popupData]);

  // Flatten rows so nested fields become columns
  const flatRows = useMemo(() => rawRows.map(r => flattenRow(r)), [rawRows]);

  // Columns = union of keys across a sample
  const autoColumns = useMemo(() => {
    const keys = new Set();
    flatRows.slice(0, 200).forEach(r => Object.keys(r || {}).forEach(k => keys.add(k)));
    return Array.from(keys);
  }, [flatRows]);

  const [visibleCols, setVisibleCols] = useState(autoColumns);
  useEffect(() => setVisibleCols(autoColumns), [autoColumns]);

  // Search
  const filteredRows = useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return flatRows;
    const cols = (visibleCols.length ? visibleCols : autoColumns);
    return flatRows.filter(row =>
      cols.some(col => {
        const v = row?.[col];
        let s = "";
        if (typeof v === "string") s = v;
        else if (v instanceof Date) s = v.toISOString();
        else if (v == null) s = "";
        else if (typeof v === "object") s = JSON.stringify(v);
        else s = String(v);
        return s.toLowerCase().includes(q);
      })
    );
  }, [flatRows, searchTerm, visibleCols, autoColumns]);

  // Paging
  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSizeState));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * pageSizeState;
  const pageRows = filteredRows.slice(start, start + pageSizeState);
  const colsToRender = visibleCols.length ? visibleCols : autoColumns;

  const toggleCol = (c) =>
    setVisibleCols(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  const selectAll = () => setVisibleCols(autoColumns);
  const selectNone = () => setVisibleCols([]);

  const formatCell = (v) => {
    if (v == null) return "—";
    if (v instanceof Date) return v.toLocaleString();
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  // CSV export of visible columns
  const exportCSV = () => {
    const rows = filteredRows;
    const cols = colsToRender;
    const esc = (s) => {
      const str = s == null ? "" : String(s);
      if (/[",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
      return str;
    };
    const header = cols.join(",");
    const body = rows.map(r => cols.map(c => {
      const v = r?.[c];
      return esc(v instanceof Date ? v.toISOString() : (typeof v === "object" ? JSON.stringify(v) : v));
    }).join(",")).join("\n");
    const csv = header + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ai-data.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Expose export so existing header button can call it
  useEffect(() => {
    window.__AI_EXPORT__ = exportCSV;
    return () => { if (window.__AI_EXPORT__ === exportCSV) delete window.__AI_EXPORT__; };
  }, [filteredRows, colsToRender]);

  return (
    <div className="ai-grid">
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <button className="btn btn-light" onClick={() => setPickerOpen(o => !o)}>Columns</button>
        <button className="btn btn-primary" onClick={exportCSV}>Export to Excel</button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => { setPage(1); setSearchTerm(e.target.value); }}
            style={{ width: 220 }}
          />
          <select
            className="form-select form-select-sm"
            value={pageSizeState}
            onChange={e => { setPage(1); setPageSize(Number(e.target.value)); }}
            style={{ width: 80 }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <div style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6, marginBottom: 8, maxHeight: 220, overflowY: "auto", display: pickerOpen ? "block" : "none" }}>
        <div style={{ marginBottom: 6 }}>
          <button className="btn btn-sm btn-light" onClick={selectAll}>Select all</button>{" "}
          <button className="btn btn-sm btn-light" onClick={selectNone}>Select none</button>
        </div>
        {autoColumns.length === 0 ? <div>No columns</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 6 }}>
            {autoColumns.map(c => (
              <label key={c} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={visibleCols.includes(c)} onChange={() => toggleCol(c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="table-wrapper" style={{ maxHeight: 420, overflow: "auto", border: "1px solid #eee", borderRadius: 6 }}>
        <table className="table table-sm" style={{ width: "100%", tableLayout: "auto" }}>
          <thead>
            <tr>
              {( (visibleCols.length ? visibleCols : ["_raw"]) ).map(c => (
                <th key={c} style={{ whiteSpace: "nowrap", position: "sticky", top: 0, background: "#f8f9fa" }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(visibleCols.length === 0) ? (
              <tr><td colSpan={1} style={{ textAlign: "left", padding: 8 }}>
                <pre style={{margin:0, maxHeight:360, overflow:"auto"}}>
                  {JSON.stringify(rawRows.slice(start, start+pageSizeState), null, 2)}
                </pre>
              </td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={visibleCols.length} style={{ textAlign: "center", padding: 16 }}>No matching items.</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#f5f5f5" }}>
                { (visibleCols.length ? visibleCols : autoColumns).map(c => (<td key={c}>{formatCell(row?.[c])}</td>)) }
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 8 }}>
        <button className="btn btn-sm btn-light" disabled={currentPage<=1} onClick={() => setPage(p => Math.max(1, p-1))}>Prev</button>
        <span style={{ fontSize: 12 }}>Page {currentPage} of {totalPages}</span>
        <button className="btn btn-sm btn-light" disabled={currentPage>=totalPages} onClick={() => setPage(p => Math.min(totalPages, p+1))}>Next</button>
      </div>
    </div>
  );
}

/* ===================== DASHBOARD ===================== */

/* UPDATED labels: added "SIM- backend send" and kept split SIM columns */
const columnLabels = [
  "Scanned", "Collections", "Del-items","Del-pats", "Walk-ins",
  "Bags on shelves", "% msg sent","Oldest bag", "Bags under 1mth", "Bags over 1mth", "Total stock £", "Exp/Slow value £","Shelf Check",
  "Fridge", "Cleaning", "Cashup", "RP Reg", "Handover", "MRHA",
  "Items/mth", "NMS Comp", "NMS Wait", "Interv", "Near Miss in 30 days", "Incidents", "Complaints","Accident book",
  "SMS Sent", "Email Sent", "Incoming", "SMS Signup", "Email Signup",
    "Use SIM msg", "Notify service", "No. SIM", "Sim-waiting", "Sim-del", "SIM- backend send", "Sim-failures", "Bulk waiting", "Bulk sent"
];

/* UPDATED keys to match backend: waitingCount, deliveredCount, backendSentCount, otherCount */
const columnKeyMap = [
  "scanned", "collections", "deliveries","drops", "walkIns",
  "bagsOnShelves","itemsWithMsg", "oldestBag", "bagsUnder1Month", "bagsOver1Month", "totalValue", "expSlowValue",
  "shelfCheck","fridge", "cleaning", "cashup", "rpReg", "handover", "MRHA",
  "itemsPerMonth",  "nmsComp", "nmsWait", "interventions", "nearMisses", "incidents", "complaints","accidents",
  "smsSent", "emailSent", "incoming", "smsSignup", "emailSignup",
   "useSimCardMessaging", "notificationService","sim_number", "waitingCount", "deliveredCount", "backendSentCount", "otherCount", "bulkWaiting", "bulkSent"
];

const Mockupp = () => {
  const eventSourceRef = useRef(null);
  const [searchText, setSearchText] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [popupData, setPopupData] = useState(null);
  const [dateMode] = useState("today");
  const todayStr = new Date().toISOString().split("T")[0];
  const [customFrom, setCustomFrom] = useState(todayStr);
  const [customTo, setCustomTo] = useState(todayStr);

  // --- Create Group (admin-only display for "Pharmace all") ---
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const orgName = localStorage.getItem("organisationName") || "";
  const canCreateGroup = orgName === "Pharmace all";

  const sortedPharmacies = useMemo(() => {
    if (!sortConfig.key) return pharmacies;
    return [...pharmacies].sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];
      const numA = parseFloat(String(valA).replace(/[%£,]/g, ""));
      const numB = parseFloat(String(valB).replace(/[%£,]/g, ""));
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortConfig.direction === "asc" ? numA - numB : numB - numA;
      }
      return sortConfig.direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [pharmacies, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getDateRange = () => {
    if (!customFrom || !customTo) {
      alert("Please select both From and To dates.");
      return null;
    }
    const fromParsed = new Date(customFrom);
    const toParsed = new Date(customTo);
    if (toParsed < fromParsed) {
      alert("'To' date must be after 'From' date.");
      return null;
    }
    fromParsed.setHours(0, 1, 0, 0);
    toParsed.setHours(23, 59, 59, 999);
    return { fromDate: fromParsed, toDate: toParsed };
  };

  // Visibility for section group headings
  // === Section visibility state + persistence (no reload) ===
const [showScan, setShowScan] = useState(() => localStorage.getItem("scanToShelf") !== "false");
const [showInventory, setShowInventory] = useState(() => localStorage.getItem("inventory") !== "false");
const [showHousekeeping, setShowHousekeeping] = useState(() => localStorage.getItem("housekeeping") !== "false");
const [showActivities, setShowActivities] = useState(() => localStorage.getItem("activities") !== "false");
const [showCommunications, setShowCommunications] = useState(() => localStorage.getItem("communications") !== "false");

const persistToggle = (key, setter) => (e) => {
  const val = !!e.target.checked;
  setter(val);
  localStorage.setItem(key, val ? "true" : "false");
};

  /* UPDATED: Communications now has 12 columns (27–38 inclusive) */
  const sectionVisibility = [
    ...Array(10).fill(showScan),           // 0–9
    ...Array(3).fill(showInventory),       // 10–12
    ...Array(6).fill(showHousekeeping),    // 13–18
    ...Array(8).fill(showActivities),      // 19–26
    ...Array(14).fill(showCommunications)  // 27–38
  ];

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.onmessage = null;
        eventSourceRef.current.onerror = null;
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      alert("Not authenticated. Please log in.");
      return;
    }
    const range = getDateRange();
    if (!range) return;

    const members = JSON.parse(localStorage.getItem("members") || "[]");
    const organisationName = localStorage.getItem("organisationName") || "";

    let pharmacyNames;
    if (organisationName === "Pharmace all") {
      pharmacyNames = [{ pharmacyName: 'xxx', pharmacyId: 'xxx' }];
    } else {
      pharmacyNames = members.map((m) => ({
        pharmacyId: m.pharmacyId,
        pharmacyName: m.pharmacyName || m.pharmacyId
      }));
    }

    setPharmacies([]);
    setProgress(0);
    setIsStreaming(true);
    setLoading(false);
    try {
      const response = await fetch("https://api.pharmacegroup.co.uk/gridpopulate-stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          fromDate: range.fromDate.toISOString(),
          toDate: range.toDate.toISOString(),
          pharmacyNames
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop(); // leave incomplete segment for next round

        for (const part of parts) {
          if (part.startsWith("data: ")) {
            const json = JSON.parse(part.replace("data: ", ""));
            if (json.progress !== undefined) setProgress(json.progress);
            if (json.grid) {
              setPharmacies((json.grid || []).sort((a, b) =>
                a.pharmacyName.localeCompare(b.pharmacyName)
              ));
              setProgress(100);
              setIsStreaming(false);
              return; // Done
            }
          }
        }
      }

      setIsStreaming(false);
    } catch (err) {
      console.error("Error during fetch:", err);
      setIsStreaming(false);
    }
  };

  // Cell colouring (incl. SIM rules on "Sim-del")
  const getCellStyle = (columnName, value, pharmacy) => {
    // Shelf / Fridge / Cleaning traffic-lighting

  if (columnName === "Near Miss" || columnName === "Near Miss in 30 days") {
    // treat null/undefined/missing as stale too
    if (!Number(pharmacy?.nearMissReviewFresh)) {
      return { backgroundColor: "gold" }; // amber
    }
  }

    if (columnName === "Shelf Check" || columnName === "Fridge" || columnName === "Cleaning") {
      if (value === "OK") return { backgroundColor: "lightgreen" };
      if (value === "!") return { backgroundColor: "gold" };
      if (value === "!!") return { backgroundColor: "coral" };
    }

    // Handover = number
    if (columnName === "Handover") {
      const numericVal = Number(value);
      if (!isNaN(numericVal)) {
        if (numericVal === 0) return { backgroundColor: "lightgreen" };
        if (numericVal > 0) return { backgroundColor: "coral" };
      }
    }

    // Green for positive counts in these columns
    const greenIfPositive = ["SMS Sent", "Email Sent", "Scanned", "Collections", "Del-items","Del-pats","Walk-ins","Near Miss", "Near Miss in 30 days"];
    if (greenIfPositive.includes(columnName)) {
      const numericVal = Number(value);
      if (!isNaN(numericVal) && numericVal > 0) {
        return { backgroundColor: "lightgreen" };
      }

      // Custom logic if 'Scanned' > 5 for comms cells
      const scannedVal = Number(pharmacy["scanned"]);
      const smsSentVal = Number(pharmacy["smsSent"]);
      const emailSentVal = Number(pharmacy["emailSent"]);

      if (!isNaN(scannedVal) && scannedVal > 5) {
        if (columnName === "SMS Sent") {
          if (smsSentVal > 5) return { backgroundColor: "lightgreen" };
          if (smsSentVal === 0 && emailSentVal === 0) return { backgroundColor: "lightcoral" };
        }
        if (columnName === "Email Sent") {
          if (emailSentVal > 5) return { backgroundColor: "lightgreen" };
          if (smsSentVal === 0 && emailSentVal === 0) return { backgroundColor: "lightcoral" };
        }
      }
    }

    // Percentage gradient (kept)
    const percentGradientFields = ["SMS Signup", "Email Signup", "% msg sent"];
    if (percentGradientFields.includes(columnName)) {
      const percent = Number(String(value).replace('%', '').trim());
      if (!isNaN(percent) && percent > 0 && percent <= 100) {
        const r = Math.round(240 - (percent * (240 - 144)) / 100);
        const g = Math.round(128 + (percent * (255 - 128)) / 100);
        const b = Math.round(128 - (percent * 128) / 100);
        return { backgroundColor: `rgb(${r}, ${g}, ${b})` };
      }
    }

    // SIM delivery logic on the "Sim-del" cell
    if (columnName === "Sim-del") {
      const delivered = Number(pharmacy?.deliveredCount ?? 0);
      const waiting = Number(pharmacy?.waitingCount ?? 0);
      const other   = Number(pharmacy?.otherCount ?? 0);
      const backend   = Number(pharmacy?.backendSentCount ?? 0);

      if (delivered > 0) return { backgroundColor: "lightgreen" };
      if (delivered === 0 && other > 0) return { backgroundColor: "lightcoral" };
      if (delivered === 0 && other === 0 && waiting > 0) return { backgroundColor: "lightcoral" };
          if (backend>0) return { backgroundColor: "lightcoral" };
    }


// Flag "Use SIM msg" red if it's No while "No. SIM" > 0
if (columnName === "Use SIM msg") {
  // value is the raw cell value for this column (boolean or "Yes"/"No")
  const useSim =
    typeof value === "boolean"
      ? value
      : String(value).toLowerCase() === "yes";

  // sim_number is the data key for the "No. SIM" column (see columnKeyMap)
  const noSimCount = Number(pharmacy?.sim_number ?? 0);

  if (!useSim && noSimCount > 0) {
    return { backgroundColor: "red", color: "white" };
  }
}



    return { backgroundColor: "white" };
  };

  // Render helper: shows booleans as Yes/No, keeps others as-is or "-"
  const displayVal = (v) => (typeof v === "boolean" ? (v ? "Yes" : "No") : (v ?? "-"));


  const handleCellClick = async (columnIndex, pharmacy) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("Not authenticated. Please log in.");
        return;
      }

      const columnName = columnLabels[columnIndex] || `Column ${columnIndex + 1}`;
      const baseRange = getDateRange();
      if (!baseRange) {
        alert("Please select a valid date range.");
        return;
      }
      const { fromDate: baseFrom, toDate: baseTo } = baseRange;
      let reqFrom = new Date(baseFrom);
      let reqTo = new Date(baseTo);

      // --- existing click handlers (unchanged) ---

      if (["Scanned", "Collections"].includes(columnName)) {
        try {
          const route = columnName === "Scanned" ? "/scanneddetails" : "/collecteddetails";
          const response = await fetch(`https://api.pharmacegroup.co.uk${route}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyName: pharmacy.pharmacyName,
              fromDate: reqFrom.toISOString(),
              toDate: reqTo.toISOString()
            })
          });
          if (!response.ok) throw new Error(`Failed to fetch ${columnName.toLowerCase()} data`);
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            chartData: result.data,
            isScannedOrCollected: true
          });
          return;
        } catch (err) { console.error(`Error fetching ${columnName.toLowerCase()} data:`, err); }
      }

      if (columnName === "Walk-ins") {
        try {
          const response = await fetch(`https://api.pharmacegroup.co.uk/walkindetails`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyName: pharmacy.pharmacyName,
              fromDate: reqFrom.toISOString(),
              toDate: reqTo.toISOString()
            })
          });
          if (!response.ok) throw new Error(`Failed to fetch ${columnName.toLowerCase()} data`);
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            chartData: result.data,
            isWalkin: true
          });
          return;
        } catch (err) { console.error(`Error fetching ${columnName.toLowerCase()} data:`, err); }
      }




      // NEW: Bulk messages (both "Bulk waiting" and "Bulk sent")
if (columnName === "Bulk waiting" || columnName === "Bulk sent") {
  try {
    const response = await fetch("https://api.pharmacegroup.co.uk/bulkdetails", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        dbName: pharmacy.pharmacyId,              // your grid stores dbName in pharmacyId
        fromDate: reqFrom.toISOString(),
        toDate: reqTo.toISOString()
      })
    });

    if (!response.ok) throw new Error(`Failed to fetch ${columnName.toLowerCase()} data`);
    const result = await response.json();

    // PopupGrid looks for p.data (among other shapes), so provide the array as `data`
    // result.details shape: [{ date, message, sent, waiting }, ...]
    setPopupData({
      column: columnName,
      pharmacyName: pharmacy.pharmacyName,
      dbName: pharmacy.pharmacyId,
      data: result.details,
      isBulk: true
    });
    return;
  } catch (err) {
    console.error(`Error fetching ${columnName.toLowerCase()} data:`, err);
  }
}


      if (columnName === "Near Miss in 30 days") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/nearmissdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });
          if (!response.ok) throw new Error("Failed to fetch near miss data");
          const result = await response.json();

          // NEAR MISS SPLIT: include pharmacyId/dbName so the right panel can call the monthly-report API
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            pharmacyId: pharmacy.pharmacyId,
            dbName: pharmacy.pharmacyId,
            nearMissDetails: result.details,
            isNearMissPopup: true
          });
          return;
        } catch (err) { console.error("Error fetching near miss data:", err); }
      }

      if (columnName === "Del-pats") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/deliverydetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyId: pharmacy.pharmacyId,
              pharmacyName: pharmacy.pharmacyName,
              fromDate: reqFrom.toISOString(),
              toDate: reqTo.toISOString()
            })
          });
          const result = await response.json();
          if (!response.ok) {
            console.error("Error fetching delivery details:", result);
            throw new Error(result?.message || "Failed to fetch delivery details");
          }
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            deliveryDetails: result,
            isDeliveryPopup: true
          });
          return;
        } catch (err) { console.error("Error fetching delivery details:", err); }
      }

      if (columnName === "Accident book") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/accidentdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });
          if (!response.ok) throw new Error("Failed to fetch accident data");
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            accidentDetails: result.accidents,
            isAccidentPopup: true
          });
          return;
        } catch (err) { console.error("Error fetching accident data:", err); }
      }

      if (columnName === "Handover") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/handoverdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            handoverDetails: result.details,
            isHandoverPopup: true
          });
          return;
        } catch (err) { console.error("Error fetching handover details:", err); }
      }

      if (columnName === "Cleaning") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/cleaninglogdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            cleaningDetails: result.details,
            isCleaningPopup: true
          });
          return;
        } catch (err) { console.error("Error fetching cleaning details:", err); }
      }

      if (columnName === "Shelf Check") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/shelfcheckdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });
          if (!response.ok) throw new Error("Failed to fetch shelf check data");
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            shelfcheckdetails: result.shelfcheckdetails,
            isShelfCheckPopup: true
          });
          return;
        } catch (err) { console.error("Error fetching shelf check details:", err); }
      }

      if (columnName === "Fridge") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/fridgelogs", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });
          if (!response.ok) throw new Error("Failed to fetch fridge logs");
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            fridgeLogs: result.logs,
            isFridgePopup: true
          });
          return;
        } catch (err) { console.error("Error fetching fridge logs:", err); }
      }

      if (["Bags on shelves", "Bags under 1mth", "Bags over 1mth"].includes(columnName)) {
        let bucket = "all";
        if (columnName === "Bags under 1mth") bucket = "under";
        if (columnName === "Bags over 1mth")  bucket = "over";

        const payload = { pharmacyId: pharmacy.pharmacyId, bucket };
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/bagsonshelf_details", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
          const text = await response.text();
          if (!response.ok) throw new Error(`Shelf breakdown failed (${response.status})`);
          let result = {};
          try { result = JSON.parse(text); } catch (e) { console.error("JSON parse error:", e, "raw:", text); throw e; }
          const shelves = (result.shelves || []).sort((a, b) =>
            (a.description || "").localeCompare(b.description || "")
          );
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            shelfDetails: shelves,
            isShelfPopup: true,
            meta: result.meta
          });
          return;
        } catch (err) {
          console.error("Error fetching shelf breakdown:", err);
          alert("Couldn't load shelf breakdown. See console for details.");
        }
      }

      if (["Total stock £", "Exp/Slow value £"].includes(columnName)) {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/stockbreakdown", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyName: pharmacy.pharmacyName,
              expSlowOnly: columnName === "Exp/Slow value £"
            })
          });
          if (!response.ok) throw new Error("Failed to fetch stock data");
          const result = await response.json();
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            stockDetails: result.details,
            isStockPopup: true
          });
          return;
        } catch (err) { console.error("Error fetching stock data:", err); }
      }

      // Default popup
      setPopupData({
        column: columnName,
        pharmacyName: pharmacy.pharmacyName,
        isShelfPopup: false
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const totals = {};
    columnKeyMap.forEach((key, index) => {
      let total = 0;
      let count = 0;
      pharmacies.forEach(pharmacy => {
        const val = pharmacy[key];
        if (typeof val === "number") {
          total += val;
          count++;
        } else if (typeof val === "string" && val.endsWith("%")) {
          const percent = parseFloat(val.replace("%", ""));
          if (!isNaN(percent)) {
            total += percent;
            count++;
          }
        }
      });
      const isPercentage = columnLabels[index].includes("Signup");
      if (count === 0) {
        totals[key] = "-";
      } else if (isPercentage) {
        totals[key] = `${Math.round(total / count)}%`;
      } else {
        totals[key] = total;
      }
    });
    return totals;
  };

const handleCreateGroup = async () => {
  if (!newOrgName || !newEmail || !newPassword) {
    alert("Please complete all fields.");
    return;
  }

  // Require a valid login token (the endpoint returns 401 without it)
  const token = localStorage.getItem("authToken");
  if (!token) {
    alert("You must be logged in to create a group (no auth token found).");
    return;
  }

  try {
    const res = await fetch("https://api.pharmacegroup.co.uk/creategroup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,   // <-- add auth header
      },
      body: JSON.stringify({
        organisation_name: newOrgName,
        email: newEmail,
        password: newPassword,
        expireBefore: 4,
        slowBefore: 26,
        members: [],
        scanToShelf: true,
        inventory: true,
        housekeeping: true,
        activities: true,
        communications: true
      })
    });

    // Helpful error handling for 401s
    if (res.status === 401) {
      const txt = await res.text();
      throw new Error(txt || "Unauthorized (401). Your session may have expired.");
    }

    const text = await res.text();
    if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

    alert("✅ Group created. You can now log in with that email/password.");
    setCreateOpen(false);
    setNewOrgName("");
    setNewEmail("");
    setNewPassword("");
  } catch (err) {
    console.error("Create group failed:", err);
    alert("Failed to create group: " + err.message);
  }
};


  return (
    <div className="mockup-container">
      <div
        className="dashboard-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <h2>Pharmacy Summary Dashboard</h2>

        {loading && !isStreaming && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}

        <div className="date-controls">
          <label style={{ marginLeft: "10px" }}>
            From:
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </label>
          <label style={{ marginLeft: "10px" }}>
            To:
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </label>
          <button style={{ marginLeft: "10px" }} onClick={fetchData}>
            🔄 Refresh analysis
          </button>
          {canCreateGroup && (
            <button
              style={{ marginLeft: "10px" }}
              onClick={() => setCreateOpen(true)}
              title="Create a new organisation"
            >
              ➕ Create group
            </button>
          )}
        </div>
      </div>

{/* Section visibility checkboxes */}
<div
  style={{
    display: "flex",
    flexWrap: "wrap",
    gap: "12px 18px",
    alignItems: "center",
    margin: "10px 0"
  }}
>
  <strong style={{ marginRight: 6 }}>Sections:</strong>

  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
    <input type="checkbox" checked={showScan} onChange={persistToggle("scanToShelf", setShowScan)} />
    <span>Scan to shelf</span>
  </label>

  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
    <input type="checkbox" checked={showInventory} onChange={persistToggle("inventory", setShowInventory)} />
    <span>Inventory</span>
  </label>

  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
    <input type="checkbox" checked={showHousekeeping} onChange={persistToggle("housekeeping", setShowHousekeeping)} />
    <span>Housekeeping</span>
  </label>

  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
    <input type="checkbox" checked={showActivities} onChange={persistToggle("activities", setShowActivities)} />
    <span>Activities</span>
  </label>

  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
    <input type="checkbox" checked={showCommunications} onChange={persistToggle("communications", setShowCommunications)} />
    <span>Communications</span>
  </label>
</div>

      <div style={{ margin: "10px 0" }}>
        <input
          type="text"
          placeholder="🔍 Search (all terms must match)..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: "60%",
            padding: "8px",
            fontSize: "14px",
            borderRadius: "6px",
            border: "1px solid #ccc"
          }}
        />
      </div>

      <div className="mockup-table">
        <table>
          <thead>
            <tr>
              <th className="sticky-col">Pharmacy</th>
              {sectionVisibility.slice(0, 10).some(Boolean) && (
                <th className="group-label bg-light" colSpan={sectionVisibility.slice(0, 10).filter(Boolean).length}>
                  Scan to shelf
                </th>
              )}
              {sectionVisibility.slice(10, 13).some(Boolean) && (
                <th className="group-label bg-dark" colSpan={sectionVisibility.slice(10, 13).filter(Boolean).length}>
                  Inventory
                </th>
              )}
              {sectionVisibility.slice(13, 19).some(Boolean) && (
                <th className="group-label bg-light" colSpan={sectionVisibility.slice(13, 19).filter(Boolean).length}>
                  Housekeeping
                </th>
              )}
              {sectionVisibility.slice(19, 27).some(Boolean) && (
                <th className="group-label bg-dark" colSpan={sectionVisibility.slice(19, 27).filter(Boolean).length}>
                  Activities
                </th>
              )}
              {/* Communications spans indices 27..38 (12 cols) */}
              {sectionVisibility.slice(27, 39).some(Boolean) && (
                <th className="group-label bg-light" colSpan={sectionVisibility.slice(27, 39).filter(Boolean).length}>
                  Communications
                </th>
              )}
            </tr>
            <tr>
              <th className="sticky-col sticky-header"></th>
              {columnLabels.map((label, idx) =>
                sectionVisibility[idx] ? (
                  <th
                    key={idx}
                    className="sticky-header"
                    onClick={() => requestSort(columnKeyMap[idx])}
                    style={{ cursor: "pointer" }}
                  >
                    {label}
                    {sortConfig.key === columnKeyMap[idx] && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                  </th>
                ) : null
              )}
            </tr>
          </thead>
          <tbody>
            {sortedPharmacies
              .filter(pharmacy => {
                const fragments = searchText.toLowerCase().split(" ").filter(f => f.trim() !== "");
                const searchable = Object.values(pharmacy).join(" ").toLowerCase();
                return fragments.every(fragment => searchable.includes(fragment));
              })
              .map((pharmacy, rowIndex) => (
                <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5" }}>
                  <td className="sticky-col">
                    {pharmacy.pharmacyName.replace(/pharmacy/i, "").trim()}
                  </td>
                  {columnKeyMap.map((key, colIndex) =>
                    sectionVisibility[colIndex]
                      ? (() => {
                          const raw = pharmacy[key];
                          const shown = displayVal(raw);
                          const cellClass = shown === "-" ? "cell pale" : "cell";
                          return (
                            <td
                              key={colIndex}
                              className={cellClass}
                              style={getCellStyle(columnLabels[colIndex], raw, pharmacy)}
                              onClick={() => handleCellClick(colIndex, pharmacy)}
                            >
                              {shown}
                            </td>
                          );
                        })()
: null
                  )}
                </tr>
              ))}

            <tr className="total-row">
              <td className="sticky-col">Total</td>
              {columnKeyMap.map((key, colIndex) =>
                sectionVisibility[colIndex] ? (
                  <td key={colIndex} className="cell">
                    {displayVal(calculateTotals()[key])}
                  </td>
                ) : null
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {isStreaming && (
        <div style={{ marginBottom: 10 }}>
          Loading data... {progress}%
          <progress value={progress} max="100" style={{ width: "100%" }} />
        </div>
      )}

      {createOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setCreateOpen(false); }}
        >
          <div
            style={{
              width: 420,
              maxWidth: "96vw",
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              overflow: "hidden",
              fontSize: 14
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
              <strong>Create New Group</strong>
              <button
                onClick={() => setCreateOpen(false)}
                style={{ background: "#111827", color: "#fff", border: "1px solid #111827", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}
              >
                Close
              </button>
            </div>

            <div style={{ padding: 12, display: "grid", gap: 10 }}>
              <label>
                <div style={{ marginBottom: 4 }}>Organisation name</div>
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 4 }}>Email</div>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 4 }}>Password</div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                />
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: 12, borderTop: "1px solid #e5e7eb" }}>
              <button onClick={() => setCreateOpen(false)} className="btn btn-light">Cancel</button>
              <button
                onClick={handleCreateGroup}
                className="btn btn-primary"
                disabled={!newOrgName || !newEmail || !newPassword}
              >
                Create group
              </button>
            </div>
          </div>
        </div>
      )}

      {popupData && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setPopupData(null)}>X</button>

            {/* NEAR MISS SPLIT: intercept Near Miss to show the 2-panel dialog */}
            {popupData?.isNearMissPopup ? (
              <NearMissSplitDialog popupData={popupData} />
            ) : (
              (popupData.isDeliveryPopup ||
               popupData.column === "Fridge" ||
               popupData.isStockPopup ||
               popupData.isShelfPopup ||
               popupData.isShelfCheckPopup ||
               popupData.isAccidentPopup ||
               popupData.isHandoverPopup ||
               popupData.isCleaningPopup ||
               popupData.isScannedOrCollected ||
               popupData.isWalkin ||
               popupData.column === "Near Miss" ||   // keep legacy path intact for safety
               popupData.isSmsBySimPopup) ? (
                <PopupContent popupData={popupData} />
              ) : (
                <PopupGrid popupData={popupData} />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Mockupp;
